/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-underscore-dangle */

import {
    ApolloClient, HttpLink, InMemoryCache, ApolloLink, from,
} from '@apollo/client';
import { RetryLink } from 'apollo-link-retry';
import fetch from 'isomorphic-unfetch';
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import { removeIsLoginFlagging } from '@helper_auth';
import getConfig from 'next/config';

// Only holds serverRuntimeConfig and publicRuntimeConfig
const config = getConfig() || {};
const { publicRuntimeConfig = {}, serverRuntimeConfig = {} } = config;

const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: {
        __schema: {
            types: [],
        },
    },
});

const host = publicRuntimeConfig?.BASE_URL || 'http://localhost:3000';
// Use direct GRAPHQL_URL on server side to avoid proxy overhead and NAT loopback timeouts
const uriInternal = serverRuntimeConfig?.GRAPHQL_URL || `${host}/graphql`;
const uri = typeof window === 'undefined' ? uriInternal : '/graphql';
// handle if token expired
const logoutLink = onError((err) => {
    const { graphQLErrors, networkError } = err;

    if (typeof window === 'undefined') return;

    if (networkError && graphQLErrors?.[0]?.status > 500) {
        window.location.href = '/maintenance';
        return;
    }

    if (graphQLErrors?.[0]?.extensions?.category === 'graphql-seller-authorization') {
        if (window.location.pathname !== '/seller/unauthorized') {
            window.location.href = '/seller/unauthorized';
        }
        return;
    }

    if (graphQLErrors?.[0]?.status === 401) {
        try {
            removeIsLoginFlagging();
            window.location.href = '/no-route';
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Logout error:', e);
        }
        return;
    }

    if (networkError?.statusCode === 413) {
        networkError.message = 'Your file is too large. Please choose smaller file size.';
    }
});

const link = new RetryLink().split(
    (operation) => operation.getContext().request === 'internal',
    new HttpLink({ uri, credentials: 'same-origin', fetch }),
    new HttpLink({
        uri, credentials: 'same-origin', fetch, useGETForQueries: true,
    }),
);

/**
 * Meddleware to customize headers
 */

export default function createApolloClient(initialState, ctx) {
    // The `ctx` (NextPageContext) will only be present on the server.
    // use it to extract auth headers (ctx.req) or similar.

    const middlewareHeader = new ApolloLink((operation, forward) => {
        const browserHeaders = ctx?.req?.headers || {};

        const extraHeaders = {};
        if (browserHeaders['x-pwa']) extraHeaders['x-pwa'] = browserHeaders['x-pwa'];

        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                ...extraHeaders,
            },
        }));
        return forward(operation).map((response) => {
            if (publicRuntimeConfig && publicRuntimeConfig.appEnv === 'local') {
                // eslint-disable-next-line no-console
                const consoleFn = response.errors ? console.error : console.log;
                consoleFn('APOLLO_LOGS ', {
                    operationName: operation.operationName,
                    query: operation.query.loc.source,
                    variables: operation.variables,
                    response,
                    queryBody: operation.query.loc.source.body.substring(0, 100),
                });
            }
            if (typeof window !== 'undefined') {
                try {
                    if (typeof window?.wzApi?.sendEvent === 'function') {
                        window.wzApi.sendEvent({
                            type: 'goal',
                            payload: {
                                url: '/graphql',
                                request: { operationName: operation?.operationName, ...(operation?.variables || {}) },
                                response,
                                method: 'POST',
                            },
                        });

                        // Process any offline calls when wzApi becomes available
                        if (Array.isArray(window.wzOfflineCalls)) {
                            window.wzOfflineCalls.forEach((call) => window.wzApi.sendEvent(call));
                            window.wzOfflineCalls = [];
                        }
                    } else {
                        // console.log('function not yet defined, adding to offline calls array');
                        if (!Array.isArray(window.wzOfflineCalls)) { window.wzOfflineCalls = []; }
                        if (window.wzOfflineCalls.length < 100) {
                            window.wzOfflineCalls.push({
                                type: 'goal',
                                payload: {
                                    url: '/graphql',
                                    request: { operationName: operation?.operationName, ...(operation?.variables || {}) },
                                    response,
                                    method: 'POST',
                                },
                            });
                        }
                    }
                    return response;
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Error in wzResponseMiddleware', error);
                }
            }
            return response;
        });
    });

    return new ApolloClient({
        ssrMode: Boolean(ctx),
        link: from([middlewareHeader, logoutLink, link]),
        cache: new InMemoryCache({ fragmentMatcher }).restore(initialState),
        // reference https://www.apollographql.com/docs/react/development-testing/developer-tooling/#apollo-client-devtools
        // eslint-disable-next-line no-underscore-dangle
        connectToDevTools:
            typeof window !== 'undefined'
            && window.__APOLLO_CLIENT__
            && typeof publicRuntimeConfig !== 'undefined'
            && publicRuntimeConfig.appEnv === 'local',
        resolvers: {},
    });
}
